import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { Contact } from '../../../../../js/api';
import { HealthcareEntitiesService } from '../../../../../js/api';
import { setFormErrorsFromApiErrors } from '../../../../../js/utils/forms';
import { Button } from '../../../components/button';
import { Field, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';

const newContactSchema = z.object({
  first_name: z.string().trim().min(1, 'First name is required'),
  last_name: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().email('Enter a valid email'),
  phone_number: z.string().trim(),
});

type NewContactData = z.infer<typeof newContactSchema>;

const FIELDS: { name: keyof NewContactData; label: string; type?: string }[] = [
  { name: 'first_name', label: 'First Name' },
  { name: 'last_name', label: 'Last Name' },
  { name: 'email', label: 'Email', type: 'email' },
  { name: 'phone_number', label: 'Phone', type: 'tel' },
];

/**
 * Creates a contact in the acting-as organization (the backend derives the org
 * from the security context) and hands back its id for selection.
 */
export const NewContactInlineForm: React.FC<{
  onCreated: (contactId: string) => void;
  onCancel: () => void;
}> = ({ onCreated, onCancel }) => {
  const form = useForm<NewContactData>({
    resolver: zodResolver(newContactSchema),
    defaultValues: { first_name: '', last_name: '', email: '', phone_number: '' },
  });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = async (data: NewContactData) => {
    try {
      const created = await HealthcareEntitiesService.healthcareEntitiesContactsCreate({
        requestBody: data as Contact,
      });
      if (created.id) onCreated(created.id);
    } catch (error) {
      setFormErrorsFromApiErrors(
        error as Parameters<typeof setFormErrorsFromApiErrors>[0],
        form.setError as Parameters<typeof setFormErrorsFromApiErrors>[1]
      );
    }
  };

  // Rendered inside the dialog's own <form>, so submit is a button click, not
  // a nested form submission.
  return (
    <div
      className="tw:space-y-3 tw:rounded-lg tw:border tw:border-zinc-200 tw:bg-zinc-50 tw:p-3"
      data-testid="new-contact-form"
    >
      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
        {FIELDS.map(({ name, label, type }) => (
          <Field key={name} className="tw:min-w-0">
            <Label htmlFor={`new-contact-${name}`}>{label}</Label>
            <Input
              id={`new-contact-${name}`}
              type={type}
              {...form.register(name)}
              data-invalid={errors[name] ? true : undefined}
            />
            {errors[name] && (
              <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">{errors[name]?.message}</p>
            )}
          </Field>
        ))}
      </div>
      {errors.root?.non_field_errors && (
        <p className="tw:text-sm tw:text-stoplight-danger">
          {errors.root.non_field_errors.message}
        </p>
      )}
      <div className="tw:flex tw:justify-end tw:gap-2">
        <Button plain small onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          small
          color="brandgreen1"
          onClick={form.handleSubmit(onSubmit)}
          disabled={isSubmitting}
          data-testid="new-contact-create"
        >
          {isSubmitting ? 'Creating...' : 'Create contact'}
        </Button>
      </div>
    </div>
  );
};
